import { useSmoothRevealCursor } from "./smooth-reveal-engine";

/**
 * One revealable part of an assistant message (a `text` or `reasoning` part), in
 * document order. `isDone` is the part's own `state === "done"`.
 */
export interface RevealablePart {
  text: string;
  isDone: boolean;
}

/**
 * Characters currently eligible to reveal across the ordered parts, **sequentially**:
 * walk the parts in order accumulating each one's full current length, but STOP after
 * the first part that is not yet `done` (its current length is included; everything
 * after it is excluded).
 *
 * This is what serializes the reveal across parts. A model emits reasoning, then the
 * answer; the reveal decouples pacing from arrival (it drains a buffer), so without
 * this the answer's own reveal would start while the reasoning tail is still draining —
 * two typewriters running at once. Gating the budget on "every earlier part is done"
 * means the single cursor can't reach a later part's region until each earlier part is
 * fully counted, so parts type out one after another.
 */
export const computeSequentialRevealTarget = (parts: readonly { length: number; isDone: boolean }[]): number => {
  let target = 0;
  for (const part of parts) {
    target += part.length;
    if (!part.isDone) {
      break;
    }
  }
  return target;
};

/**
 * The cursor position to seed on an ANIMATING mount — including a mid-stream refresh /
 * stream re-attach of a turn that is still streaming. It is the summed length of the
 * leading contiguous already-`done` parts (i.e. the offset of the first not-yet-done part).
 *
 * Seeding here rather than at 0 is what keeps a re-attach from blanking: a completed leading
 * part (e.g. a finished `reasoning` part, which re-mounts collapsed) renders fully at once, and
 * only the actively-streaming part types out from its start. Seeding at 0 would instead replay
 * the whole done prefix through the single cursor while it sits invisible behind the collapsed
 * reasoning — leaving the assistant body blank until that prefix drains.
 */
export const computeInitialCursor = (parts: readonly RevealablePart[]): number => {
  let cursor = 0;
  for (const part of parts) {
    if (!part.isDone) {
      break;
    }
    cursor += part.text.length;
  }
  return cursor;
};

/**
 * The start offset of the segment (part) the cursor is currently at or within: the largest
 * part-start offset `<= cursor`. Fed to the reveal engine's prompt carve-out so the first
 * character of each part is guaranteed the moment the cursor reaches that part's boundary —
 * mid-part it returns the current part's start (so the carve-out stays dormant and never skips
 * chars), and at a boundary it returns the new part's start (so its first char is prompted
 * instead of stalling while the velocity re-ramps from ~0).
 */
export const segmentOriginAtCursor = (lengths: readonly number[], cursor: number): number => {
  let origin = 0;
  let offset = 0;
  for (const length of lengths) {
    if (offset > cursor) {
      break;
    }
    origin = offset;
    offset += length;
  }
  return origin;
};

/**
 * Given the ordered part lengths and a single reveal cursor over their concatenation,
 * return how many characters of each part are revealed:
 * `clamp(floor(cursor) - offset_i, 0, length_i)`, where `offset_i` is the sum of the
 * earlier parts' lengths. A part shows nothing until the cursor has passed every earlier
 * part — so a later part never renders live characters while an earlier part is still
 * revealing.
 */
export const slicePartsAtCursor = (lengths: readonly number[], cursor: number): number[] => {
  const flooredCursor = Math.floor(cursor);
  const counts: number[] = [];
  let offset = 0;
  for (const length of lengths) {
    counts.push(Math.max(0, Math.min(length, flooredCursor - offset)));
    offset += length;
  }
  return counts;
};

/**
 * Message-level sequential reveal (B-1446 follow-up).
 *
 * {@link useSmoothStreamedText} reveals a single string; an assistant message, though,
 * has several revealable parts (a `reasoning` part then a `text` part, sometimes more).
 * Running an independent reveal per part lets a later part's reveal overlap an earlier
 * part's drain — the answer types while reasoning is still typing. This hook instead runs
 * ONE velocity-smoothed cursor for the whole message (via the shared
 * {@link useSmoothRevealCursor} engine), walking the parts in order, so a later part can't
 * begin revealing until every earlier part has finished. It is a thin adapter: it supplies
 * the sequential target, the re-attach seed, and a per-part prompt origin; the engine owns
 * the cadence, drain, reduced-motion, and mount lifecycle.
 *
 * @param parts       Ordered revealable parts (`text` / `reasoning`), each with `isDone`.
 * @param isStreaming Whether this message is the actively-streaming turn.
 * @returns The visible prefix of each part's text, in the same order as `parts`.
 */
export const useSequentialPartReveal = (parts: readonly RevealablePart[], isStreaming: boolean): string[] => {
  const lengths = parts.map((part) => part.text.length);
  const total = lengths.reduce((sum, length) => sum + length, 0);
  const target = computeSequentialRevealTarget(
    parts.map((part) => ({ length: part.text.length, isDone: part.isDone }))
  );

  const cursor = useSmoothRevealCursor({
    active: isStreaming,
    target,
    total,
    initialCursor: computeInitialCursor(parts),
    promptOriginFor: (revealed) => segmentOriginAtCursor(lengths, revealed),
  });

  const counts = slicePartsAtCursor(lengths, Math.min(cursor, total));
  return parts.map((part, index) => part.text.slice(0, counts[index]));
};
